import { useEffect } from "react";

import useLayoutConfigStore from "store/layout-config-store/useLayoutConfigStore";

import { useEmailAuth } from "hooks/useEmailAuth";
import { useState } from "react";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import useToastStore from "store/toast-store/useToastStore";
import axiosService from "services/axios";

const EMAIL_REGEX = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

const withEmailCheckBlocker = (Component: React.ComponentType<any>) => {
  return ({ variant, onVerified }: { variant: "init" | "update"; onVerified?: () => void }) => {
    const [email, setEmail] = useState<string | null>(null);
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [isEmailSending, setIsEmailSending] = useState<boolean>(false);
    const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
    const [otp, setOtp] = useState<string | null>(null);
    const [subOrgId, setSubOrgId] = useState<string | undefined>(undefined);
    const [userId, setUserId] = useState<string | undefined>(undefined);
    const [isOtpValid, setIsOtpValid] = useState<boolean>(false);
    const [isOtpVerifying, setIsOtpVerifying] = useState<boolean>(false);
    const [retryCount, setRetryCount] = useState<number>(0);
    const {
      verifyEmailAuth,
      createWalletWithEmail,
      createEmailAuthSession,
      initUserEmailRecovery,
      setCredential,
    } = useEmailAuth();
    const {
      tgUserName,
      email: userEmail,
      tgUserId,
      organizationId,
      refreshLatestUserState,
      logoutTgUserV2,
    } = useUserStoreV2();
    const { setIsShowFooter } = useLayoutConfigStore();
    const { showToast } = useToastStore();

    useEffect(() => {
      setIsShowFooter(false);

      return () => {
        setIsShowFooter(true);
      };
    }, []);

    const handleOnEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      setIsEmailValid(!!e.target.value.match(EMAIL_REGEX));
    };

    const handleOnOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setOtp(e.target.value);
      setIsOtpValid(!!e.target.value);
    };

    const handleSendEmail = async () => {
      if (!email) {
        return;
      }
      setIsEmailSending(true);

      if (retryCount > 1) {
        onClickLogout();
        return;
      }

      try {
        let newSubOrgId;
        if (!organizationId || organizationId === subOrgId) {
          await createWalletWithEmail(email, subOrgId);
          const user = await axiosService.getUser(tgUserId!.toString());
          newSubOrgId = user?.organizationId;
          setSubOrgId(newSubOrgId);
        }

        let turnkeyUserId;
        if (variant === "init") {
          turnkeyUserId = await createEmailAuthSession(email, newSubOrgId);
        } else {
          await initUserEmailRecovery(email);
          turnkeyUserId = await createEmailAuthSession(email);
        }

        if (!turnkeyUserId) {
          showToast({
            variant: "error",
            message: `Incorrect email address. Please try again.`,
            duration: 5000,
          });
          return;
        }

        setUserId(turnkeyUserId);
        setIsEmailSent(true);
        showToast({ variant: "success", message: `Email sent to ${email}`, duration: 5000 });
      } catch (e) {
        console.error(e);
      } finally {
        setIsEmailSending(false);
        setRetryCount(retryCount + 1);
      }
    };

    const handleGoBack = async () => {
      setIsEmailSent(false);
    };

    const handleVerifyOTP = async () => {
      if (!otp || !userId) return;

      setIsOtpVerifying(true);
      try {
        const _email = email || userEmail;
        await verifyEmailAuth(otp, userId, _email);
        showToast({
          variant: "success",
          message: "Email authenticated.",
          duration: 5000,
        });
        await refreshLatestUserState();
        setCredential((pre) => ({ ...pre, isSessionExpired: false }));
        if (variant === "update") {
          if (onVerified) {
            onVerified();
          }
        }
      } catch (e) {
        console.error(e);
        showToast({
          variant: "error",
          message: "Incorrect one-time security code. Please try again.",
          duration: 5000,
        });
      } finally {
        setIsOtpVerifying(false);
        setIsShowFooter(true);
        setIsEmailSent(false);
      }
    };

    const onClickLogout = async () => {
      logoutTgUserV2();
    };
    return (
      <Component
        variant={variant}
        onVerified={onVerified}
        onClickLogout={onClickLogout}
        isEmailValid={isEmailValid}
        isEmailSending={isEmailSending}
        isEmailSent={isEmailSent}
        tgUserId={tgUserId}
        tgUserName={tgUserName}
        email={email}
        userEmail={userEmail}
        isOtpValid={isOtpValid}
        isOtpVerifying={isOtpVerifying}
        handleOnOTPChange={handleOnOTPChange}
        handleSendEmail={handleSendEmail}
        handleOnEmailChange={handleOnEmailChange}
        handleGoBack={handleGoBack}
        handleVerifyOTP={handleVerifyOTP}
      />
    );
  };
};

export default withEmailCheckBlocker;
